import { useCallback, useMemo, useReducer } from "react";

type Action =
  | {
      type: "update";
      payload: {
        key: string;
        value: any;
      };
    }
  | {
      type: "reset";
      payload?: never;
    };

type FilterFunc<T = any> = (data: T, value?: any) => boolean;

type FilterRules<T extends string = string> = Record<T, FilterFunc>;

function getDefaultState<T>(rules: FilterRules) {
  return Object.keys(rules).reduce((state, key) => ({ ...state, [key]: null }), {} as T);
}

function reducer<T extends Record<string, any>>(state: T, action: Action) {
  switch (action.type) {
    case "reset":
      return getDefaultState(state);
    case "update":
      return {
        ...state,
        [action.payload.key]: action.payload.value,
      };
    default:
      return state;
  }
}

export function useDataFilter<T = any, R extends FilterRules = FilterRules>(data: T[], rules: R) {
  type StateType = { [K in keyof R]: any };
  const [state, dispatch] = useReducer<StateType, [Action]>(
    reducer as (state: StateType, action: Action) => StateType,
    getDefaultState(rules)
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      return (Object.keys(rules) as Array<keyof R>).reduce((result, key) => {
        const filtered = state[key as keyof StateType]
          ? rules[key](item, state[key as keyof StateType])
          : true;
        return result && filtered;
      }, true);
    });
  }, [data, state]);

  const filter = useCallback(
    (key: keyof R, value: any) => {
      dispatch({ type: "update", payload: { key: key as string, value } });
    },
    [dispatch]
  );

  const reset = useCallback(() => {
    dispatch({ type: "reset" });
  }, [dispatch]);

  return { data: filteredData, filter, reset, state };
}
